/* eslint-disable no-useless-catch */
import { ENV } from "../utils";

export class Syllabu {
  baseApi = ENV.BASE_API;

  /**
   * Fetches all syllabus.
   *
   * This function sends a GET request to the syllabu endpoint using the provided access token for authentication.
   * It retrieves all syllabus.
   * If the request is successful, it returns the list of syllabus.
   * If there is an error during the request, it throws the error.
   *
   * @async
   * @function getSyllabus
   * @param {string} accessToken - The access token used to authenticate the request.
   * @returns {Promise<Array>} - The list of all syllabus.
   * @throws {Object} - Throws an error if the request fails.
   */
  async getSyllabus(accessToken) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.SYLLABU}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches a specific syllabu.
   *
   * This function sends a GET request to the syllabu endpoint using the provided access token for authentication.
   * It retrieves the syllabu for the specified syllabu ID.
   * If the request is successful, it returns the syllabu object.
   * If there is an error during the request, it throws the error.
   *
   * @async
   * @function getSyllabu
   * @param {string} accessToken - The access token used to authenticate the request.
   * @param {string} syllabuId - The ID of the syllabu to fetch.
   * @returns {Promise<Object>} - The syllabu object for the specified syllabu ID.
   * @throws {Object} - Throws an error if the request fails.
   */
  async getSyllabu(accessToken, syllabuId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.SYLLABU}/${syllabuId}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches the subjects by grade for a specific syllabu.
   *
   * This function sends a GET request to the syllabu subject grade endpoint using the provided access token for authentication.
   * It retrieves the subjects by grade for the specified syllabu subject grade ID.
   * If the request is successful, it returns the subjects by grade.
   * If there is an error during the request, it throws the error.
   *
   * @async
   * @function getSubjectGradeBySyllabu
   * @param {string} accessToken - The access token used to authenticate the request.
   * @param {string} syllabuSubjectGradeId - The ID of the syllabu subject grade.
   * @returns {Promise<Object>} - The subject grade for the specified syllabu subject grade ID.
   * @throws {Object} - Throws an error if the request fails.
   */
  async getSubjectGradeBySyllabu(accessToken, syllabuSubjectGradeId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.SYLLABU}/getSubjectGrade/${syllabuSubjectGradeId}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Creates a new syllabu.
   *
   * This function sends a POST request to the syllabu endpoint with the provided data and access token for authentication.
   * If the request is successful, it returns the newly created syllabu object.
   * If there is an error during the request, it throws the error.
   *
   * @async
   * @function createSyllabu
   * @param {string} accessToken - The access token used to authenticate the request.
   * @param {Object} data - The data for the new syllabu.
   * @returns {Promise<Object>} - The newly created syllabu object.
   * @throws {Object} - Throws an error if the request fails.
   */
  async createSyllabu(accessToken, data) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.SYLLABU}`;
      const params = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 201) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Updates an existing syllabu.
   *
   * This function sends a PATCH request to the syllabu endpoint with the provided data and access token for authentication.
   * If the request is successful, it returns the updated syllabu object.
   * If there is an error during the request, it throws the error.
   *
   * @async
   * @function updateSyllabu
   * @param {string} accessToken - The access token used to authenticate the request.
   * @param {string} syllabuId - The ID of the syllabu to update.
   * @param {Object} data - The updated data for the syllabu.
   * @returns {Promise<Object>} - The updated syllabu object.
   * @throws {Object} - Throws an error if the request fails.
   */
  async updateSyllabu(accessToken, syllabuId, data) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.SYLLABU}/${syllabuId}`;
      const params = {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Deletes an existing syllabu.
   *
   * This function sends a DELETE request to the syllabu endpoint using the provided access token for authentication.
   * If the request is successful, it returns the result of the deletion.
   * If there is an error during the request, it throws the error.
   *
   * @async
   * @function deleteSyllabu
   * @param {string} accessToken - The access token used to authenticate the request.
   * @param {string} syllabuId - The ID of the syllabu to delete.
   * @returns {Promise<Object>} - The result of the deletion.
   * @throws {Object} - Throws an error if the request fails.
   */
  async deleteSyllabu(accessToken, syllabuId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.SYLLABU}/${syllabuId}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches the subjects for a specific syllabu and grade.
   *
   * This function sends a GET request to the syllabu subject grade endpoint using the provided access token for authentication.
   * It retrieves the subjects for the specified syllabu and grade ID.
   * If the request is successful, it returns the subjects.
   * If there is an error during the request, it throws the error.
   *
   * @async
   * @function getSyllabuSubjectGrades
   * @param {string} accessToken - The access token used to authenticate the request.
   * @param {string} syllabuId - The ID of the syllabu.
   * @param {string} gradeId - The ID of the grade.
   * @returns {Promise<Object>} - The subjects for the specified syllabu and grade ID.
   * @throws {Object} - Throws an error if the request fails.
   */
  async getSyllabuSubjectGrades(accessToken, syllabuId, gradeId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.SYLLABU}/subjectGrade/${syllabuId}/${gradeId}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Creates a new syllabu subject grade.
   *
   * This function sends a POST request to the syllabu subject grade endpoint with the provided data and access token for authentication.
   * If the request is successful, it returns the created syllabu subject grade object.
   * If there is an error during the request, it throws the error.
   *
   * @async
   * @function createSyllabuSubjectGrade
   * @param {string} accessToken - The access token used to authenticate the request.
   * @param {Object} data - The data for the new syllabu subject grade.
   * @returns {Promise<Object>} - The created syllabu subject grade object.
   * @throws {Object} - Throws an error if the request fails.
   */
  async createSyllabuSubjectGrade(accessToken, data) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.SYLLABU}/subjectGrade`;
      const params = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 201) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Deletes an existing syllabu subject grade.
   *
   * This function sends a DELETE request to the syllabu subject grade endpoint using the provided access token for authentication.
   * If the request is successful, it returns the result of the deletion.
   * If there is an error during the request, it throws the error.
   *
   * @async
   * @function deleteSyllabuSubjectGrade
   * @param {string} accessToken - The access token used to authenticate the request.
   * @param {string} syllabuSubjectGradeId - The ID of the syllabu subject grade to delete.
   * @returns {Promise<Object>} - The result of the deletion.
   * @throws {Object} - Throws an error if the request fails.
   */
  async deleteSyllabuSubjectGrade(accessToken, syllabuSubjectGradeId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.SYLLABU}/subjectGrade/${syllabuSubjectGradeId}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }
}
